import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, API_URL } from '../../utils/api';
// Phase 7 sign-off: profiles email, display_name, short_text.
import { MAX_LENGTHS } from '../../utils/inputValidation';

export default function SubscribersTab() {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | { mode: 'edit', item }
  const [form, setForm] = useState({ email: '', name: '', locale: 'ko' });
  const [toast, setToast] = useState(null);
  const perPage = 30;

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: perPage, active_only: activeOnly });
      const res = await apiFetch(`/api/v1/admin/subscribers?${params}`);
      if (res.ok) {
        const data = await res.json();
        setSubscribers(data.subscribers || []);
        setTotal(data.total || 0);
      }
    } catch { /* ignore */ }
  }, [page, activeOnly]);

  useEffect(() => { load(); }, [load]);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = async () => {
    if (!form.email) return;
    try {
      const res = await apiFetch('/api/v1/admin/subscribers', {
        method: 'POST',
        body: JSON.stringify({ email: form.email, name: form.name || null, locale: form.locale }),
      });
      if (res.ok) {
        showToast('success', 'Subscriber added');
        setModal(null);
        setForm({ email: '', name: '', locale: 'ko' });
        load();
      } else {
        const data = await res.json();
        showToast('error', data.detail || 'Failed');
      }
    } catch { showToast('error', 'Network error'); }
  };

  const handleUpdate = async () => {
    if (!modal?.item) return;
    try {
      const res = await apiFetch(`/api/v1/admin/subscribers/${modal.item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: form.name, locale: form.locale, is_active: form.is_active }),
      });
      if (res.ok) {
        showToast('success', 'Subscriber updated');
        setModal(null);
        load();
      } else {
        const data = await res.json();
        showToast('error', data.detail || 'Failed');
      }
    } catch { showToast('error', 'Network error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscriber permanently?')) return;
    try {
      const res = await apiFetch(`/api/v1/admin/subscribers/${id}`, { method: 'DELETE' });
      if (res.ok) { showToast('success', 'Deleted'); load(); }
      else showToast('error', 'Failed to delete');
    } catch { showToast('error', 'Network error'); }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('veripass_token');
      const url = `${API_URL}/api/v1/admin/subscribers/export`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `subscribers_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { showToast('error', 'Export failed'); }
  };

  const openEdit = (item) => {
    setForm({ name: item.name || '', locale: item.locale || 'ko', is_active: item.is_active });
    setModal({ mode: 'edit', item });
  };

  const filtered = search
    ? subscribers.filter(s => s.email.toLowerCase().includes(search.toLowerCase()) || (s.name || '').toLowerCase().includes(search.toLowerCase()))
    : subscribers;

  const totalPages = Math.ceil(total / perPage);

  return (
    <div>
      {toast && (
        <div className={`em-toast em-toast-${toast.type}`}>
          {toast.msg}
          <button onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="em-toolbar">
        <input
          type="text"
          maxLength={MAX_LENGTHS.short_text}
          placeholder="Search email or name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
          <input type="checkbox" checked={activeOnly} onChange={e => { setActiveOnly(e.target.checked); setPage(1); }} />
          Active only
        </label>
        <div style={{ flex: 1 }} />
        <button className="em-btn" onClick={handleExport}>Export CSV</button>
        <button className="em-btn em-btn-primary" onClick={() => { setForm({ email: '', name: '', locale: 'ko' }); setModal('add'); }}>
          + Add Subscriber
        </button>
      </div>

      <table className="em-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Source</th>
            <th>Locale</th>
            <th>Subscribed</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(s => (
            <tr key={s.id}>
              <td>{s.email}</td>
              <td>{s.name || '—'}</td>
              <td>{s.source}</td>
              <td>{s.locale}</td>
              <td>{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : '—'}</td>
              <td>
                <span className={`em-badge ${s.is_active ? 'em-badge-active' : 'em-badge-inactive'}`}>
                  {s.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <button className="em-btn em-btn-sm" onClick={() => openEdit(s)}>Edit</button>{' '}
                <button className="em-btn em-btn-sm em-btn-danger" onClick={() => handleDelete(s.id)}>Delete</button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>No subscribers found</td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="em-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span>Page {page} of {totalPages} ({total} total)</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <div className="em-modal-overlay" onClick={() => setModal(null)}>
          <div className="em-modal" onClick={e => e.stopPropagation()}>
            <h3>{modal === 'add' ? 'Add Subscriber' : 'Edit Subscriber'}</h3>
            {modal === 'add' && (
              <>
                <label>Email *</label>
                <input type="text" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} maxLength={MAX_LENGTHS.email} />
              </>
            )}
            <label>Name</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={MAX_LENGTHS.display_name} />
            <label>Locale</label>
            <select value={form.locale} onChange={e => setForm(f => ({ ...f, locale: e.target.value }))}>
              <option value="ko">Korean (ko)</option>
              <option value="en">English (en)</option>
            </select>
            {modal !== 'add' && (
              <>
                <label style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />{' '}
                  Active
                </label>
              </>
            )}
            <div className="em-modal-actions">
              <button className="em-btn" onClick={() => setModal(null)}>Cancel</button>
              <button className="em-btn em-btn-primary" onClick={modal === 'add' ? handleAdd : handleUpdate}>
                {modal === 'add' ? 'Add' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
